/**
 * BUILT-THING-IS-A-TAB-1 — the things this account has had built, for the menu.
 *
 * OPTIONAL AUTH, deliberately, for the same reason `useRecordTypes` takes that
 * seam: the nav is rendered in tests and shells that never mounted the provider.
 * No account means no business to ask, which resolves to no tabs and a menu
 * byte identical to today.
 *
 * THREE STATES, AND THE MENU ONLY EVER DRAWS THE THIRD. While we are still
 * asking, and when the read failed, the menu shows NOTHING rather than a guess.
 * A tab that appears and then vanishes is worse than one that arrives late, and
 * a tab invented from a failed read is a promise of a screen that may not exist.
 * The page itself says which of the three it is; the menu cannot say anything.
 */

import { useEffect, useState } from 'react'
import { useOptionalAuth } from '../providers/AuthProvider'
import { listArtifacts, type MerchantArtifact } from '../lib/artifacts/artifactsRepo'
import type { BuiltThing } from '../lib/artifacts/artifactNav'
import { sharedRead } from '../lib/sharedRead'

export type BuiltThingsState =
  | { state: 'loading' }
  | { state: 'failed'; error: string }
  | { state: 'ready'; things: MerchantArtifact[] }

export function useBuiltThings(): BuiltThingsState {
  const subAccountId = useOptionalAuth()?.currentSubAccountId ?? null
  const [state, setState] = useState<BuiltThingsState>({ state: 'loading' })

  useEffect(() => {
    let cancelled = false
    if (!subAccountId) {
      setState({ state: 'ready', things: [] })
      return
    }
    setState({ state: 'loading' })
    // THE-HOME-LOADS-FAST-1 — one read of this account's built things, joined
    // by every menu surface mounted beside this one.
    void sharedRead(`built-things:${subAccountId}`, () => listArtifacts(subAccountId))
      .then((rows: MerchantArtifact[]) => {
        if (!cancelled) setState({ state: 'ready', things: rows })
      })
      .catch((e: unknown) => {
        // `listArtifacts` THROWS rather than returning emptiness, on purpose, so
        // a failed read can never be drawn as "nothing built".
        if (!cancelled) {
          setState({ state: 'failed', error: e instanceof Error ? e.message : 'read failed' })
        }
      })
    return () => {
      cancelled = true
    }
  }, [subAccountId])

  return state
}

/** The tabs a state may contribute. Only a completed read contributes any. */
export function thingsForMenu(state: BuiltThingsState): BuiltThing[] {
  return state.state === 'ready' ? state.things : []
}
